import {
  Auth as NuxtAuth,
  type OpenIDConnectConfigurationDocument,
  type OpenIDConnectSchemeOptions,
  type SchemePartialOptions,
} from "@nuxtjs/auth-next";
import { Auth, ConfigurationDocument, IdToken, OpenIDConnectScheme, Storage } from "@nuxtjs/auth-next/dist/runtime";
import { type JWK, decodeProtectedHeader, errors, importJWK, jwtVerify } from "jose";

import { $config } from "@/utils/accessors/config";
import cookieDomain from "@/utils/cookie-domain";
import { OK } from "@/utils/enums/http-codes";
import { MINUTE } from "@/utils/enums/time";
import logger, { logLevel } from "@/utils/logger";
import { keysToCamel } from "@/utils/transform/object";

/**
 * Add type hinting to the application that an authenticated user is of type AuthUserSigil
 */
declare module "vue/types/vue" {
  interface Auth extends NuxtAuth {
    user: (AuthUserSigil & NuxtAuth["user"]) | (Auth0UserTokenResponse & NuxtAuth["user"]) | null;
  }
}

declare module "@nuxt/types" {
  interface Auth extends NuxtAuth {
    user: (AuthUserSigil & NuxtAuth["user"]) | (Auth0UserTokenResponse & NuxtAuth["user"]) | null;
  }
}

declare module "vuex/types/index" {
  interface Auth extends NuxtAuth {
    user: (AuthUserSigil & NuxtAuth["user"]) | (Auth0UserTokenResponse & NuxtAuth["user"]) | null;
  }
}

interface JWKsConfigurationDocument {
  keys: JWK[];
}

const newUserMinuteThreshold = 5;
const newUserTimeDelta = newUserMinuteThreshold * MINUTE;

export const isAuth0UserToken = (
  user: AuthUserSigil | Auth0UserTokenResponse | null,
): user is Auth0UserTokenResponse => {
  return user?.sub?.split("|").length === 2 && "http://shopltk.com/profile" in user;
};

export const isNewUser = (user: AuthUserSigil | Auth0UserTokenResponse | null) => {
  if (!isAuth0UserToken(user)) {
    return false;
  }

  const createdAt = new Date(user["http://shopltk.com/profile"].userCreatedAt ?? 0).getTime();
  return createdAt >= Date.now() - newUserTimeDelta;
};

export const getSsoMethod = (user: AuthUserSigil | Auth0UserTokenResponse | null) => {
  if (!isAuth0UserToken(user)) {
    return "";
  }

  const provider = user["http://shopltk.com/profile"].authnProvider;
  switch (provider) {
    case "auth0":
      return "email";

    case "google-oauth2":
      return "google";

    default:
      return provider;
  }
};

class IdTokenCamel extends IdToken {
  // Transform JWT keys into camelCase to be compatible with API mutated values
  // Do not modify the JWT itself as this will invalidate it as being tampered.
  userInfo() {
    const rawToken = super.userInfo() as JsonObject;
    return keysToCamel(rawToken);
  }
}

class JWKConfigurationDocument {
  public $storage: Storage;
  public scheme: OpenIDConnectScheme;
  public key: string;

  constructor(scheme: OpenIDConnectScheme, storage: Storage) {
    this.scheme = scheme;
    this.$storage = storage;
    this.key = "_jwk_document." + this.scheme.name;
  }

  _set(value: false | JWKsConfigurationDocument) {
    return this.$storage.setState(this.key, value);
  }

  get() {
    return this.$storage.getState(this.key) as undefined | false | JWKsConfigurationDocument;
  }

  set(value: JWKsConfigurationDocument) {
    this._set(value);
    return value;
  }

  reset() {
    this._set(false);
  }

  async request() {
    // Client Side Hydration
    const jwks: JWKsConfigurationDocument = this.scheme.$auth.ctx?.nuxtState?.$auth?.oidc?.jwks;
    if (process.client && jwks) {
      this.set(jwks);
    }

    // Abandon Early
    if (this.get()) {
      return;
    }

    const uri = this.scheme.configurationDocument.get().jwks_uri;
    if (uri) {
      // clone axios to remove all plugins
      const client = this.scheme.requestHandler.axios.create();
      const jwks = await client.$get<JWKsConfigurationDocument>(uri);

      // Store state for hydration
      if (process.server) {
        this.scheme.$auth.ctx.beforeNuxtRender(({ nuxtState }) => {
          const oidc = Object.assign({}, nuxtState.$auth?.oidc, { jwks });
          nuxtState.$auth = { oidc };
        });
      }

      this.set(jwks);
    }
  }

  async init() {
    const idToken = this.scheme.idToken.get();
    if (typeof idToken !== "string") {
      logger(logLevel.DEBUG, "No JWT available. Skipping fetching JWKs");
      return;
    }

    try {
      await this.request();
    } catch (error) {
      const message = "No JWT available. Skipping fetching JWKs";
      logger("error", message, { error });
      throw new Error(message, { cause: error });
    }

    await this.validate(idToken);
  }

  async validate(idToken: string) {
    const jwtHeader = decodeProtectedHeader(idToken);
    const jwkDocument = this.get();
    if (!jwkDocument) {
      logger(logLevel.WARN, "Unable to verify JWT. Configuration has not yet been loaded");
      throw new Error("No matching JWK to validate against");
    }

    const key = jwkDocument.keys.find((key) => key.kid === jwtHeader.kid);
    if (key === undefined) {
      const kids = jwkDocument.keys.map(({ kid }) => kid);
      logger(logLevel.ERROR, "Unable to verify JWT. No matching JWK returned from public endpoint. Removing ID Token", {
        available: kids,
        needed: jwtHeader.kid,
      });
      throw new Error("No matching JWK to validate against");
    }

    const jwk = await importJWK(key);
    try {
      await jwtVerify(idToken, jwk);
    } catch (error) {
      // Log unexpected validation errors. JWTs expire naturally and is not an error log worth event
      if (!(error instanceof errors.JWTExpired)) {
        logger(logLevel.ERROR, "JWT Signature Invalid", {}, error);
      }
      throw new Error("JWT signature is invalid", { cause: error });
    }
  }
}

class LooseConfigurationDocument extends ConfigurationDocument {
  set(value: boolean | OpenIDConnectConfigurationDocument) {
    if (typeof value !== "boolean") {
      value.scopes_supported?.push("ltk.consumer");
    }

    return super.set(value);
  }

  async request() {
    // Fixing bug in Client Side Hydration
    const serverDoc: OpenIDConnectConfigurationDocument =
      this.scheme.$auth.ctx?.nuxtState?.$auth?.oidc?.configurationDocument;
    if (process.client && serverDoc) {
      this.set(serverDoc);
    }

    await super.request();
  }
}

/**
 * Local extension of Auth0 Provider swapping the Oauth2 scheme for the OpenIDConnect scheme.
 * Extends some functionality to allow for validating JWT tokens with the public keys available
 * from Auth0.
 *
 * Will also augment auth0 config with runtime values:
 *
 * - Add environment prefixes to cookie and localstorage namespaces (except in prod)
 * - Set cookie domain to match vanity domain
 */
export default class LTKAuth0Scheme<OptionsT extends OpenIDConnectSchemeOptions> extends OpenIDConnectScheme {
  jwksDocument: JWKConfigurationDocument;

  constructor($auth: Auth, options: SchemePartialOptions<OptionsT>) {
    // Inline dynamic variables. They must be available at CI build time, which doesn't work for non-prod
    // environments based on the existing setup. Would need to run a single build per environment to resolve
    // the issue, which is counter to our typical development process.
    Object.assign(options, $config.auth0, {
      redirectUri: `https://${$config.ltkSite}${$auth.options.redirect.callback}`,
      logoutRedirectUri: `https://${$config.ltkSite}/`,
    });

    options.endpoints = Object.assign(options.endpoints ?? {}, $config.auth0.endpoints, {
      authorization: `https://${$config.auth0.domain}/authorize`,
      userInfo: `https://${$config.auth0.domain}/userinfo`,
      token: `https://${$config.auth0.domain}/oauth/token`,
      logout: `https://${$config.auth0.domain}/v2/logout`,
      configuration: `https://${$config.auth0.domain}/.well-known/openid-configuration`,
      changePassword: `https://${$config.auth0.domain}/dbconnections/change_password`,
    });

    const hostname = $config.ltkSite;
    const domain = cookieDomain(hostname);

    if ($auth.options.cookie) {
      $auth.options.cookie.options = Object.assign({}, $auth.options.cookie.options, {
        domain,
      });

      if ($config.env !== "prod") {
        const envPrefix = `${$config.env}.`;

        if (!$auth.options.cookie.prefix.startsWith(envPrefix)) {
          $auth.options.cookie.prefix = `${envPrefix}${$auth.options.cookie.prefix}`;
        }

        if ($auth.options.localStorage !== false && !$auth.options.localStorage.prefix.startsWith(envPrefix)) {
          $auth.options.localStorage.prefix = `${envPrefix}${$auth.options.localStorage.prefix}`;
        }
      }
    }

    super($auth, options as OptionsT);

    // Customize ID Token Behavior
    this.idToken = new IdTokenCamel(this, this.$auth.$storage);
    this.configurationDocument = new LooseConfigurationDocument(this, this.$auth.$storage);
    this.jwksDocument = new JWKConfigurationDocument(this, this.$auth.$storage);
  }

  async changePassword(user: Auth0UserTokenResponse | null): Promise<boolean> {
    if (!isAuth0UserToken(user) || !user.email || !user["http://shopltk.com/profile"].authnCanChangeEmail) {
      logger(logLevel.WARN, "Auth0 password reset attempted without, or with incorrect, user token", {}, { user });
      return false;
    }

    try {
      const url = (this.options.endpoints.changePassword as string) || "";
      const payload = { client_id: $config.auth0.client_id, connection: $config.auth0.connection, email: user.email };
      const response = await this.requestHandler.axios.post(url, payload);

      return response.status === OK;
    } catch (error) {
      logger(logLevel.ERROR, "Auth0 failed to initiate a password reset", {}, { user, error });
      return false;
    }
  }

  // Plucked from Auth0Scheme.
  logout() {
    this.$auth.reset();

    const url = new URL(this.options.endpoints.logout || "");

    url.searchParams.set("client_id", this.options.clientId + "");
    url.searchParams.set("returnTo", this.logoutRedirectURI);

    window.location.replace(url.toString());
  }

  reset() {
    super.reset();
    this.jwksDocument.reset();
  }

  // Extended from OpenIDConnectScheme. Some in between hooks were necessary, and as such,
  // simply calling super.mounted() was not a viable option.
  async mounted() {
    const { tokenExpired, refreshTokenExpired } = this.check(true);
    if (refreshTokenExpired || (tokenExpired && this.options.autoLogout)) {
      this.$auth.reset();
    }

    await this.configurationDocument.init();
    try {
      await this.jwksDocument.init();
    } catch (error) {
      logger(logLevel.DEBUG, "ID Token Validation Failed. Falling back to getting userinfo from remote API", {}, error);
      this.idToken.reset();
    }

    this.requestHandler.initializeRequestInterceptor(this.options.endpoints.token);
    const redirected = await this._handleCallback();
    if (!redirected) {
      return this.$auth.fetchUserOnce();
    }
  }
}
